import { GlobalState, useStateMachine } from 'little-state-machine';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Col, Row } from 'reactstrap';
import { FormFieldHeader } from '../../../../../components/FormFieldHeader';
import { IconButton } from '../../../../../components/IconButton';
import { IconSpan } from '../../../../../components/IconSpan';
import { RichTextEditor } from '../../../../../components/RichTextEditor';
import { CreateJobDto } from '../../../../../services/applicant_tracking/JobService/CreateJobDto';
import { buildUrl, replaceFinalSegment } from '../../../../../utils/url';
import { JobPostPageHeader } from './JobPostPageHeader';
import { JobAndJobPost, initCreateJobDto } from '..';
import { getReferrer } from '../referrers';
import { LocationInput, loadLocationOptions } from './LocationInput';
import { EmploymentTypeInput } from './EmploymentTypeInput';
import { SelectOption } from '../../../../../components/Select';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { FormErrorMessage } from '../../../../../components/FormErrorMessage';
import { handleFormErrors } from '../../../../../services/ApiService/handler';
import { JobDetails } from '../../../../../services/applicant_tracking/JobService/EditJobDto';
import { AreaRichTextComposer } from '../../../../../components/RichTextEditor/composers/AreaRichTextComposer';
import { DeprecatedPanel } from '../../../../../components/DeprecatedPanel';
import { ExperienceLevelSelect } from './ExperienceLevelSelect';

interface PropTypes {
  setupStep: number;
  setSetupStep: (newStep: number, jobDTO: CreateJobDto) => void;
  editorState?: string;
  updateLocalStorage: (state: GlobalState, payload: any) => GlobalState;
  initJobDto: CreateJobDto;
  isEditFlow: boolean;
  jobDetails: JobDetails;
  onSubmit: (state: CreateJobDto) => Promise<JobAndJobPost>;
}

const EDITOR_DEFAULT_STATE = `{"root":{"children":[{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"👋 About Us","type":"text","version":1}],"direction":"ltr","format":"start","indent":0,"type":"heading","version":1,"tag":"h3"},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Use this introductory section to hook candidates. Include a brief description of the company's mission, industry, stage and products/services. Keep it concise.","type":"text","version":1}],"direction":"ltr","format":"start","indent":0,"type":"paragraph","version":1},{"children":[],"direction":null,"format":"","indent":0,"type":"heading","version":1,"tag":"h3"},{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"💼 About the Role","type":"text","version":1}],"direction":"ltr","format":"start","indent":0,"type":"heading","version":1,"tag":"h3"},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"A brief overview of the role and its purpose within the organisation. Indicate who the person will be working closely with. Help candidates see the bigger picture and understand how the role impacts the business.","type":"text","version":1}],"direction":"ltr","format":"start","indent":0,"type":"paragraph","version":1},{"children":[],"direction":null,"format":"","indent":0,"type":"heading","version":1,"tag":"h3"},{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"🔥 Key Responsibilities","type":"text","version":1}],"direction":"ltr","format":"start","indent":0,"type":"heading","version":1,"tag":"h3"},{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"A list of the role's main day-to-day activities and core responsibilities, presented in bullet point format","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"listitem","version":1,"value":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Rank it in order of importance","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"listitem","version":1,"value":2},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Keep it detailed but concise","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"listitem","version":1,"value":3}],"direction":"ltr","format":"","indent":0,"type":"list","version":1,"listType":"bullet","start":1,"tag":"ul"},{"children":[],"direction":null,"format":"","indent":0,"type":"heading","version":1,"tag":"h3"},{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"😊 About You","type":"text","version":1}],"direction":"ltr","format":"start","indent":0,"type":"heading","version":1,"tag":"h3"},{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Create a bullet point list of the soft and hard skills required for the role","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"listitem","version":1,"value":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"This can include previous job experience, education/qualifications, technical skills, soft skills etc","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"listitem","version":1,"value":2},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Rank it in order of importance","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"listitem","version":1,"value":3},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Make it concise","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"listitem","version":1,"value":4}],"direction":"ltr","format":"","indent":0,"type":"list","version":1,"listType":"bullet","start":1,"tag":"ul"},{"children":[],"direction":null,"format":"","indent":0,"type":"heading","version":1,"tag":"h3"},{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"⛳️ Compensation, Perks & Benefits","type":"text","version":1}],"direction":"ltr","format":"start","indent":0,"type":"heading","version":1,"tag":"h3"},{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"£$€ Base salary & OTE","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"listitem","version":1,"value":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"List in a bullet point format all benefits & perks the successful candidate will have access to","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"listitem","version":1,"value":2}],"direction":"ltr","format":"","indent":0,"type":"list","version":1,"listType":"bullet","start":1,"tag":"ul"},{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}`;

export function SetupJobPostPage(props: PropTypes) {
  const navigate = useNavigate();
  const updateLocalStorage = props.updateLocalStorage;
  const { actions, state } = useStateMachine({ updateLocalStorage });
  const [createJobStore, setCreateJobStore] = useState<GlobalState>(state);

  const {
    handleSubmit,
    control,
    formState,
    reset,
    getValues,
    resetField,
    setError,
    watch,
  } = useForm<CreateJobDto>({
    mode: 'onChange',
    defaultValues: props.initJobDto,
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [locationOptions, setLocationOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    (async () => {
      setLocationOptions(await loadLocationOptions());

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    reset(props.initJobDto);
  }, [props.initJobDto]);

  const onSubmit = handleSubmit(async () => {
    try {
      const newstate = { ...state.setupJob, ...getValues() };
      const { jobPost } = await props.onSubmit(newstate);
      const nextStepUrl = replaceFinalSegment('application_form');

      actions.updateLocalStorage({ ...newstate, jobPostId: jobPost.id });

      props.setSetupStep(props.setupStep + 1, state.setupJob);
      navigate(nextStepUrl);
    } catch (e: unknown) {
      handleFormErrors(e, setError);
    }
  });

  const updateGlobalStateCallback = useCallback(
    (data: any) => {
      const updatedState = {
        ...createJobStore,
        setupJob: { ...createJobStore.setupJob, ...getValues(), ...data },
      };
      setCreateJobStore(updatedState);
      actions.updateLocalStorage(updatedState.setupJob);
    },
    [createJobStore],
  );

  const previewUrl = buildUrl(
    `${window.location.origin}/applicant_tracking/job_posts/preview`,
    [
      { key: 'name', value: watch('name') },
      { key: 'department_id', value: watch('departmentId') },
      { key: 'location_id', value: watch('locationId') },
      { key: 'location_type', value: watch('locationType') },
    ],
  );
  const onNavigate = handleSubmit(async () => {
    actions.updateLocalStorage({ ...getValues() });
  });

  return (
    <DeprecatedPanel className={'px-4 pt-4 pb-5 mb-5'}>
      <form onSubmit={onSubmit}>
        <FormErrorMessage error={formState.errors} />
        <JobPostPageHeader
          formState={formState}
          jobDetails={props.jobDetails}
          setupStep={props.setupStep}
          setSetupStep={props.setSetupStep}
          onCancel={() => {
            actions.updateLocalStorage(initCreateJobDto);
            window.location.href = getReferrer();
          }}
          onNavigate={onNavigate}
          isEditFlow={props.isEditFlow}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <LocationInput
              formControl={control}
              options={locationOptions}
              formResetField={resetField}
            />
            <EmploymentTypeInput
              formControl={control}
              formResetField={resetField}
            />
            <ExperienceLevelSelect formControl={control} />
            <FormFieldHeader
              fieldName={'Description'}
              isRequired={true}
              classNames={'fw-semibold mt-4'}
              fontSize={'fs-3'}
            />
            <small className='text-muted'>
              Add job description, requirements and benefits
            </small>
            <Row className='mt-3'>
              <Col xs={8}>
                <RichTextEditor<CreateJobDto>
                  formHookContext={{
                    controllerName: 'editorState',
                    formControl: control,
                    required: true,
                  }}
                  render={(field) => (
                    <AreaRichTextComposer
                      defaultEditorState={
                        (field.value as string) || EDITOR_DEFAULT_STATE
                      }
                      size='lg'
                      onChangeJson={(value: string) => field.onChange(value)}
                      onChangeHtml={(html: string) =>
                        updateGlobalStateCallback({ editorStateHtml: html })
                      }
                    />
                  )}
                />
              </Col>
              <Col xs={4}>
                <IconSpan
                  spanText={
                    'Consider adding sections for job description, requirements and perks and benefits.'
                  }
                  icon={{
                    name: 'bi-info-circle',
                    className: 'text-info-500 me-1',
                  }}
                  className={'fw-normal fs-5 bg-light-info px-3 py-2 rounded'}
                />
              </Col>
            </Row>
            <IconButton
              iconName={'bi-box-arrow-up-right'}
              color={'secondary'}
              buttonClass={'text-info border-info mt-4'}
              buttonText={'Preview Job Post'}
              disabled={!formState.isValid}
              onClick={() => {
                window.open(previewUrl, '_blank');
              }}
            />
          </>
        )}
      </form>
    </DeprecatedPanel>
  );
}
